import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription, finalize, forkJoin } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDateRange, OuiRangeDatePicker, OuiTextfield } from 'omnium-ui/form-field';
import {
  ProxeemaAtlasInvestorProfile,
  ProxeemaSignatureAnomaly,
  ProxeemaSignatureAnomalyReport,
  ProxeemaSignatureStateCategory,
  ProxeemaSignatureStateOption,
  ProxeemaUniversignIndexStatus,
} from '../../../../core/models/api.models';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { appRoutePaths } from '../../../../app.paths';
import { environment } from '../../../../../environments/environment';

type InvestorAnomalyGroup = {
  investorId: number;
  profile?: ProxeemaAtlasInvestorProfile | null;
  anomalies: ProxeemaSignatureAnomaly[];
};

@Component({
  selector: 'dbw-proxeema-signature-anomalies-page',
  imports: [
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    OuiButtonModule,
    OuiTextfield,
    OuiRangeDatePicker,
    StateCardComponent,
  ],
  templateUrl: './proxeema-signature-anomalies.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './proxeema-signature-anomalies.component.scss',
})
export class ProxeemaSignatureAnomaliesPageComponent implements OnInit, OnDestroy {
  readonly appRoutePaths = appRoutePaths;
  // oui-range-date-picker ne porte que des jours : les heures sont saisies à part,
  // faute de timepicker dans omnium-ui.
  readonly period = new FormControl<OuiDateRange | null>({ start: this.daysAgo(30), end: new Date() });
  readonly startTime = signal('00:00');
  readonly endTime = signal('23:59');
  readonly report = signal<ProxeemaSignatureAnomalyReport | undefined>(undefined);
  readonly loading = signal(false);
  readonly exporting = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly filter = signal<'all' | 'ged-missing' | 'signature-only'>('all');
  readonly reason = signal('');
  readonly actionInProgress = signal<string | undefined>(undefined);
  readonly stateOptions = signal<Record<ProxeemaSignatureStateCategory, ProxeemaSignatureStateOption[]>>({
    Der: [],
    Qpi: [],
    Recueil: [],
  });
  readonly filteredAnomalies = computed(() => {
    const anomalies = [...(this.report()?.anomalies ?? []), ...this.extraDocuments()];
    switch (this.filter()) {
      case 'ged-missing':
        return anomalies.filter(anomaly => !anomaly.gedFound);
      case 'signature-only':
        return anomalies.filter(anomaly => anomaly.gedFound);
      default:
        return anomalies;
    }
  });
  readonly groupedAnomalies = computed<InvestorAnomalyGroup[]>(() => {
    const groups = new Map<number, InvestorAnomalyGroup>();
    for (const anomaly of this.filteredAnomalies()) {
      const group = groups.get(anomaly.investorId);
      if (group) {
        group.anomalies.push(anomaly);
      } else {
        groups.set(anomaly.investorId, {
          investorId: anomaly.investorId,
          profile: anomaly.profile,
          anomalies: [anomaly],
        });
      }
    }
    return [...groups.values()];
  });
  // Les correspondances reviennent dans le rapport : elles sont servies par l'index local, donc
  // gratuites. Reste à savoir si la période consultée est indexée — sinon « aucune correspondance »
  // ne voudrait rien dire.
  readonly indexStatus = signal<ProxeemaUniversignIndexStatus | undefined>(undefined);
  readonly requestingIndexing = signal(false);
  readonly extraDocuments = signal<ProxeemaSignatureAnomaly[]>([]);
  readonly loadingExtraDocuments = signal(false);
  readonly checkFailures = computed(() => {
    const report = this.report();
    const signature = report?.signatureCheckUnavailableCount ?? 0;
    const ged = report?.gedCheckFailedCount ?? 0;
    return { signature, ged, total: signature + ged };
  });
  readonly metrics = computed(() => {
    const anomalies = this.filteredAnomalies();
    return {
      total: (this.report()?.anomalies.length ?? 0) + this.extraDocuments().length,
      filtered: anomalies.length,
      affectedInvestors: this.groupedAnomalies().length,
      gedMissing: anomalies.filter(anomaly => !anomaly.gedFound).length,
      signatureNotTracked: anomalies.filter(anomaly => !anomaly.signatureTrackingFound).length,
    };
  });
  readonly canUpdateState = computed(() => this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureStateUpdate));
  readonly canCancelGed = computed(() => this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureGedCancel));
  readonly canCancelUniversign = computed(() => this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureUniversignCancel));
  readonly canConfirmUniversign = computed(() => this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureUniversignConfirm));

  private stateOptionsSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
  ) {}

  ngOnInit(): void {
    this.stateOptionsSubscription = forkJoin({
      Der: this.apiService.getProxeemaSignatureStateOptions('Der'),
      Recueil: this.apiService.getProxeemaSignatureStateOptions('Recueil'),
      Qpi: this.apiService.getProxeemaSignatureStateOptions('Qpi'),
    }).subscribe({
      next: options => this.stateOptions.set(options),
      error: error => this.error.set(error?.error?.detail ?? 'Impossible de charger le catalogue des états.'),
    });
  }

  ngOnDestroy(): void {
    this.stateOptionsSubscription?.unsubscribe();
  }

  stateOptionsFor(anomaly: ProxeemaSignatureAnomaly): ProxeemaSignatureStateOption[] {
    if (!anomaly.trackedByAvancement) {
      return [];
    }

    // L'état courant est déjà proposé via l'option « Conserver ».
    return (this.stateOptions()[anomaly.category] ?? []).filter(option => option.value !== anomaly.stateValue);
  }

  backofficeUrl(profile?: ProxeemaAtlasInvestorProfile | null): string | undefined {
    if (!profile?.consultantCode || !profile?.investorCode) {
      return undefined;
    }

    const baseUrl = environment.proxeemaBackofficeUrl.replace(/\/+$/, '');
    return `${baseUrl}/investisseurs/from-list/${profile.consultantCode}/${profile.investorCode}/vue-d-ensemble`;
  }

  load(): void {
    const range = this.getRange();
    if (!range) return;
    this.loading.set(true);
    this.error.set(undefined);
    this.extraDocuments.set([]);
    this.refreshIndexStatus();
    this.apiService.getProxeemaSignatureAnomalies(range.since, range.until)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: report => this.report.set(report),
        error: error => this.error.set(error?.error?.detail ?? 'La détection des anomalies a échoué.'),
      });
  }

  exportCsv(): void {
    const range = this.getRange();
    if (!range) return;
    this.exporting.set(true);
    this.apiService.exportProxeemaSignatureAnomalies(range.since, range.until)
      .pipe(finalize(() => this.exporting.set(false)))
      .subscribe({
        next: blob => this.saveBlob(
          blob,
          `anomalies-signature-${range.since.slice(0, 10)}-${range.until.slice(0, 10)}.csv`),
        error: error => this.error.set(error?.error?.detail ?? 'L’export CSV a échoué.'),
      });
  }

  onStartTimeChange(value: string): void { this.startTime.set(value); }
  onEndTimeChange(value: string): void { this.endTime.set(value); }
  onFilterChange(value: string): void { this.filter.set(value as 'all' | 'ged-missing' | 'signature-only'); }
  onReasonChange(value: string): void { this.reason.set(value); }

  cancelGed(anomaly: ProxeemaSignatureAnomaly): void {
    if (!this.validateReason() || !window.confirm(`Annuler la signature GED du document ${anomaly.documentId} ?`)) return;
    this.runAction(
      `ged-${anomaly.documentId}`,
      this.apiService.cancelProxeemaGedSignature(anomaly.investorId, anomaly.documentId, { reason: this.reason().trim() }),
      result => result.cancelled,
      () => this.removeAnomalyLocally(anomaly));
  }

  cancelUniversign(anomaly: ProxeemaSignatureAnomaly, transactionId: string): void {
    if (!this.validateReason() || !window.confirm(`Annuler la transaction Universign ${transactionId} ?`)) return;
    this.runAction(
      `universign-${transactionId}`,
      this.apiService.cancelProxeemaUniversignTransaction(transactionId, { reason: this.reason().trim() }),
      () => true,
      // Annuler la transaction ne résout pas l'anomalie : seule la correspondance disparaît.
      () => this.updateAnomalyLocally(anomaly, {
        universignMatches: anomaly.universignMatches.filter(match => match.transactionId !== transactionId),
      }));
  }

  confirmUniversign(anomaly: ProxeemaSignatureAnomaly, transactionId: string): void {
    if (!this.validateReason() || !window.confirm(`Confirmer la transaction Universign ${transactionId} ?`)) return;
    const request = { category: anomaly.category, expectedState: anomaly.stateValue, universignTransactionId: transactionId, reason: this.reason().trim() };
    const operation = anomaly.trackedByAvancement
      ? this.apiService.confirmProxeemaUniversignSignature(anomaly.investorId, anomaly.documentId, request)
      : this.apiService.confirmProxeemaUniversignDocument(anomaly.investorId, anomaly.documentId, request);
    this.runAction(
      `confirm-${anomaly.documentId}`,
      operation,
      result => result.confirmed,
      () => this.removeAnomalyLocally(anomaly),
      result => result.error);
  }

  /** Un segment en attente ou en cours : l'index se remplit encore. */
  readonly indexingInProgress = computed(() =>
    (this.indexStatus()?.segments ?? []).some(segment => segment.status === 'Pending' || segment.status === 'Running'));

  refreshIndexStatus(): void {
    const range = this.getRange();
    if (!range) return;
    this.apiService.getProxeemaUniversignIndexStatus(range.since, range.until)
      .subscribe({ next: status => this.indexStatus.set(status) });
  }

  requestIndexing(): void {
    const range = this.getRange();
    if (!range) return;
    this.requestingIndexing.set(true);
    this.apiService.requestProxeemaUniversignIndexing(range.since, range.until)
      .pipe(finalize(() => this.requestingIndexing.set(false)))
      .subscribe({
        next: () => this.refreshIndexStatus(),
        error: error => this.error.set(
          error?.error?.detail ?? "La demande d'indexation Universign a échoué."),
      });
  }

  loadExtraDocuments(): void {
    const range = this.getRange();
    if (!range) return;
    this.loadingExtraDocuments.set(true);
    this.error.set(undefined);
    this.apiService.getProxeemaExtraDocuments(range.since, range.until)
      .pipe(finalize(() => this.loadingExtraDocuments.set(false)))
      .subscribe({
        next: documents => this.extraDocuments.set(documents),
        error: error => this.error.set(
          error?.error?.detail ?? 'La recherche des documents supplémentaires a échoué.'),
      });
  }

  downloadUniversignDocument(anomaly: ProxeemaSignatureAnomaly, transactionId: string, fileName: string): void {
    const key = `download-${transactionId}`;
    this.actionInProgress.set(key);
    this.apiService.downloadProxeemaUniversignDocument(anomaly.investorId, anomaly.documentId, transactionId)
      .pipe(finalize(() => this.actionInProgress.set(undefined)))
      .subscribe({
        next: blob => this.saveBlob(blob, fileName || `document-${transactionId}.pdf`),
        error: error => this.error.set(error?.error?.detail ?? 'Le téléchargement du document Universign a échoué.'),
      });
  }

  updateState(anomaly: ProxeemaSignatureAnomaly, value: string): void {
    const newValue = Number(value);
    if (!this.validateReason() || !Number.isInteger(newValue) || newValue === anomaly.stateValue || !window.confirm('Modifier cet état Minotaure ?')) return;
    this.runAction(
      `state-${anomaly.documentId}`,
      this.apiService.updateProxeemaSignatureState(anomaly.investorId, anomaly.category, { expectedValue: anomaly.stateValue, newValue, reason: this.reason().trim() }),
      () => true,
      () => this.applyStateChangeLocally(anomaly, newValue));
  }

  /**
   * Un document toujours « en signature » reste une anomalie : la ligne est conservée avec son
   * nouvel état. Tout autre état la résout, et la ligne disparaît.
   */
  private applyStateChangeLocally(anomaly: ProxeemaSignatureAnomaly, newValue: number): void {
    const option = (this.stateOptions()[anomaly.category] ?? []).find(item => item.value === newValue);

    if (option?.code.startsWith('SIGNING_PENDING')) {
      this.updateAnomalyLocally(anomaly, {
        stateValue: option.value,
        stateCode: option.code,
        stateLabel: option.label,
      });
      return;
    }

    this.removeAnomalyLocally(anomaly);
  }

  private removeAnomalyLocally(anomaly: ProxeemaSignatureAnomaly): void {
    this.report.update(report => report && {
      ...report,
      anomalies: report.anomalies.filter(item => !this.isSameAnomaly(item, anomaly)),
    });
    this.extraDocuments.update(items => items.filter(item => !this.isSameAnomaly(item, anomaly)));
  }

  private updateAnomalyLocally(
    anomaly: ProxeemaSignatureAnomaly,
    changes: Partial<ProxeemaSignatureAnomaly>): void {
    const apply = (item: ProxeemaSignatureAnomaly) =>
      this.isSameAnomaly(item, anomaly) ? { ...item, ...changes } : item;

    this.report.update(report => report && { ...report, anomalies: report.anomalies.map(apply) });
    this.extraDocuments.update(items => items.map(apply));
  }

  private isSameAnomaly(left: ProxeemaSignatureAnomaly, right: ProxeemaSignatureAnomaly): boolean {
    return left.investorId === right.investorId
      && left.documentId === right.documentId
      && left.category === right.category;
  }

  private getRange(): { since: string; until: string } | undefined {
    const period = this.period.value;
    const since = this.atTime(period?.start, this.startTime(), false);
    const until = this.atTime(period?.end, this.endTime(), true);
    if (!since || !until || until <= since || until.valueOf() - since.valueOf() > 180 * 86400000) {
      this.error.set('Sélectionnez une période valide de 180 jours maximum.');
      return undefined;
    }
    return { since: since.toISOString(), until: until.toISOString() };
  }

  /**
   * Combine un jour et une heure « HH:mm » en date locale. La borne de fin couvre la minute
   * entière, sans quoi « Au 23:59 » exclurait les 59 dernières secondes de la période.
   */
  private atTime(date: Date | undefined, time: string, endOfMinute: boolean): Date | undefined {
    if (!date) return undefined;
    const [hours, minutes] = time.split(':').map(Number);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return undefined;
    const combined = new Date(date);
    combined.setHours(hours, minutes, endOfMinute ? 59 : 0, endOfMinute ? 999 : 0);
    return Number.isNaN(combined.valueOf()) ? undefined : combined;
  }

  private validateReason(): boolean {
    if (this.reason().trim() && this.reason().trim().length <= 500) return true;
    this.error.set('Un motif de correction de 500 caractères maximum est requis.');
    return false;
  }

  /**
   * Après une correction, on met le tableau à jour localement au lieu de relancer la recherche :
   * une nouvelle détection prend des dizaines de secondes sur une période large. Le motif de
   * correction est conservé, plusieurs lignes s'enchaînant le plus souvent avec le même.
   */
  private runAction<T>(
    key: string,
    action: import('rxjs').Observable<T>,
    success: (result: T) => boolean,
    onSuccess: () => void,
    getError?: (result: T) => string | null | undefined): void {
    this.actionInProgress.set(key);
    action.pipe(finalize(() => this.actionInProgress.set(undefined))).subscribe({
      next: result => {
        if (!success(result)) { this.error.set(getError?.(result) ?? 'L’opération a échoué.'); return; }
        onSuccess();
      },
      error: error => this.error.set(error?.error?.detail ?? 'L’opération a échoué.'),
    });
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  private daysAgo(days: number): Date { const date = new Date(); date.setDate(date.getDate() - days); return date; }
}